import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { get, has, isEqual } from 'lodash';

import { findNearest } from '../helpers/numbers';

const DEFAULT_CURRENT_PAGE = 0;
const DEFAULT_ORDER_BY = '';
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZES = [10,20,50,100];
const DEFAULT_PAGE_SIZE_KEY = 'pageSize';
const DEFAULT_SEARCH_KEY = 'name';
const DEFAULT_SORTING = [];

export default function useTableParams(
    payload,
    {
        defaultOrderBy = DEFAULT_ORDER_BY,
        defaultPageSize = DEFAULT_PAGE_SIZE,
        pageSizes = DEFAULT_PAGE_SIZES,
        pageSizeKey = DEFAULT_PAGE_SIZE_KEY,
        searchKey = DEFAULT_SEARCH_KEY
    } = {}
) {
    const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
    const history = useHistory();
    const location = useLocation();
    const [sorting, setSorting] = useState(DEFAULT_SORTING);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [totalCount, setTotalCount] = useState(0);

    /**
     * Current page handler that sets the new current page value into the
     * querystring and reloads the page.
     */
    const onCurrentPageChange = useCallback((newPageNum) => {
        newPageNum = parseInt(newPageNum, 10);
        const newParams = new URLSearchParams(location.search);
        newParams.set('page', newPageNum);
        history.push({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }, [history, location.pathname, location.search]);

    /**
     * Page size handler that sets the new page size value into the querystring
     * and reloads the page.
     */
    const onPageSizeChange = useCallback((newPageSize) => {
        newPageSize = parseInt(newPageSize, 10);
        setPageSize(newPageSize);
        const newParams = new URLSearchParams(location.search);
        newParams.set(pageSizeKey, newPageSize);
        history.replace({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }, [history, location.pathname, location.search]);

    /**
     * Sorting change handler that sets the new page size value into the
     * querystring and reloads the page.
     */
    const onSortingChange = useCallback((sorting = []) => {
        /**
         * The DevExtreme table appears to support sorting by multiple columns
         * but the backend does not.
         */
        const [orderByParam] = sorting
            .filter(({ direction }) => direction)
            .map(
                ({ columnName, direction}) => `${columnName} ${direction}`
            );
        const newParams = new URLSearchParams(location.search);

        if (!orderByParam || orderByParam === defaultOrderBy) {
            newParams.delete('orderby');
        } else {
            newParams.set('orderby', orderByParam);
        }
        history.replace({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }, [history, location.pathname, location.search]);

    /**
     * Search term change handler that sets the search query value into the
     * querystring and reloads the page.
     */
    const onSearch = useCallback((query) => {
        const newParams = new URLSearchParams(location.search);
        if (query) {
            newParams.set(searchKey, query);
        } else {
            newParams.delete(searchKey);
        }
        newParams.delete('page');
        history.replace({
            pathname: location.pathname,
            search:   newParams.toString()
        });
    }, [history, location.pathname, location.search]);

    const onExit = useCallback(() => {
        const params = new URLSearchParams(location.search);

        params.delete('pageSize');
        params.delete('page');
        params.delete('limit');
        params.delete('startTimestamp');
        params.delete('endTimestamp');

        history.replace({
            search: params.toString()
        });
    }, [history]);

    /**
     * Derive table state from the querystring
     */
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        // Derive current page
        const currentPageParam = params.get('page') || DEFAULT_CURRENT_PAGE;
        setCurrentPage(parseInt(currentPageParam, 10));

        // Derive page sizes
        const pageSizeParam = params.get(pageSizeKey);
        if (pageSizeParam) {
            setPageSize(parseInt(pageSizeParam, 10));
        } else if (!pageSize) {
            setPageSize(
                findNearest(
                    get(payload, 'data.length', DEFAULT_PAGE_SIZE),
                    pageSizes
                )
            );
        }

        const getTotalCount = (payload) => {
            if (!payload) {
                return 0;
            }
            try {
                if (has(payload, 'totalCount') && Number.isInteger(payload.totalCount)) {
                    return payload.totalCount;
                } else if (payload.meta
                    && has(payload.meta, 'totalCount')
                    && Number.isInteger(payload.meta.totalCount)) {
                    return payload.meta.totalCount;
                } else if (payload.data && Array.isArray(payload.data)) {
                    return payload.data.length;
                }
                return 0;
            } catch (e) {
                console.debug(`error parsing totalCount: ${e}`);// eslint-disable-line no-console
                return 0;
            }
        };

        // Derive total count
        const totalCountValue = getTotalCount(payload);
        setTotalCount(totalCountValue);

        // Derive sorting
        const orderBy = params.get('orderby') || defaultOrderBy;
        if (orderBy) {
            const [columnName, direction] = orderBy.split(' ');
            const newSorting = [{ columnName, direction }];
            if (!isEqual(sorting, newSorting)) {
                setSorting(newSorting);
            }
        } else if (!isEqual(sorting, DEFAULT_SORTING)) {
            setSorting(DEFAULT_SORTING);
        }
    }, [defaultOrderBy, location.search, payload, pageSizes]);

    return {
        currentPage,
        onCurrentPageChange,
        onExit,
        onPageSizeChange,
        onSearch,
        onSortingChange,
        pageSize,
        pageSizes,
        sorting,
        totalCount
    };
}
